import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import OcRequestStatusBadge from '../../components/OrganoidRequest/OcRequestStatusBadge';
import OcTransitionBar from '../../components/OrganoidRequest/OcTransitionBar';
import OcStageNav from '../../components/OrganoidRequest/OcStageNav';
import OcHandoffNotes from '../../components/OrganoidRequest/OcHandoffNotes';
import OcWorkspaceShell from '../../components/OrganoidRequest/OcWorkspaceShell';
import AssigneeUserSelect from '../../components/OrganoidRequest/AssigneeUserSelect';
import {
  buildLabClientConfirmationPayload,
  buildPreparationMetaPayload,
  filterAcceptedCandidates,
  filterConfirmableCandidates,
  formatCandidateOcLabel,
  getOcTechDecisionLabel,
  getNextPreparationAction,
  getPreparationOwnerId,
  getPreparationStatusLabel,
  getPrimaryTransitionForPage,
  getWorkflowHandoffNotes,
  toDateInputValue,
  todayDateInputValue,
} from '../../utils/ocRequest';
import { useOcRequestWorkspace } from '../../utils/useOcRequestWorkspace';
import { showToast } from '../../config_and_helpers/helpers';
import { logError, logInfo } from '../../config_and_helpers/logger';

const LOG_CONTEXT = 'OcRequestPreparationPage';

/**
 * Preparation stage: start once from CLIENT_APPROVED, advance each accepted OC
 * via …/candidates/{id}/preparation/, then ship / complete at request level.
 * Per row: owner (thaw→ship), thawed/sent dates, next prep step.
 * While AWAITING_CLIENT_CONFIRMATION, lab can Verify for the client.
 */
const OcRequestPreparationPage = () => {
  const { api, requestId, basePath, mode, readOnly } = useOcRequestWorkspace();
  const [request, setRequest] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [advancingId, setAdvancingId] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    const [detailRes, candRes, assigneesRes] = await Promise.all([
      api.getDetail(),
      api.getCandidates(),
      api.getAssigneeCandidates(),
    ]);
    setLoading(false);
    if (!detailRes.success) {
      showToast(detailRes.messages?.[0] || 'Load failed', 'error', 'top-right', 4000);
      return;
    }
    setRequest(detailRes.data);
    setCandidates(candRes.success ? candRes.data || [] : []);
    setAssigneeOptions(assigneesRes.success ? assigneesRes.data || [] : []);
    logInfo(LOG_CONTEXT, 'preparation page loaded', {
      requestId,
      mode,
      status: detailRes.data?.status,
      candidateCount: candRes.success ? (candRes.data || []).length : null,
      assigneeCount: assigneesRes.success ? (assigneesRes.data || []).length : null,
      handoffNoteCount: getWorkflowHandoffNotes(detailRes.data?.workflow_actions)
        .length,
      confirmableCount: filterConfirmableCandidates(
        candRes.success ? candRes.data : []
      ).length,
    });
  }, [api, requestId, mode]);

  useEffect(() => {
    load();
  }, [load]);

  const accepted = useMemo(
    () => filterAcceptedCandidates(candidates),
    [candidates]
  );
  const confirmable = useMemo(
    () => filterConfirmableCandidates(candidates),
    [candidates]
  );

  const awaitingClient = request?.status === 'AWAITING_CLIENT_CONFIRMATION';

  const mergeCandidate = useCallback(updated => {
    if (!updated?.id) return;
    setCandidates(prev =>
      prev.map(c => (c.id === updated.id ? { ...c, ...updated } : c))
    );
  }, []);

  /**
   * Apply preparation POST response: full request detail; refresh candidates from
   * nested list when present, otherwise re-fetch.
   */
  const applyPrepResponse = useCallback(
    async detail => {
      setRequest(detail);
      if (Array.isArray(detail?.candidates)) {
        setCandidates(detail.candidates);
        return;
      }
      const candRes = await api.getCandidates();
      if (candRes.success) {
        setCandidates(candRes.data || []);
      }
    },
    [api]
  );

  /**
   * PATCH preparation metadata (owner / dates) for one candidate.
   *
   * @param {Object} candidate
   * @param {Object} fields
   * @param {{ silent?: boolean }} [opts]
   */
  const patchMeta = async (candidate, fields, opts = {}) => {
    if (!api) return null;
    setSavingId(candidate.id);
    const payload = buildPreparationMetaPayload(fields);
    logInfo(LOG_CONTEXT, 'patch preparation meta', {
      requestId,
      candidateId: candidate.id,
      keys: Object.keys(payload),
    });
    const result = await api.patchCandidate(candidate.id, payload);
    setSavingId(null);
    if (!result.success) {
      logError(LOG_CONTEXT, 'patch preparation meta failed', {
        candidateId: candidate.id,
        messages: result.messages,
      });
      if (!opts.silent) {
        showToast(
          result.messages?.[0] || 'Could not save preparation details',
          'error',
          'top-right',
          4000
        );
      }
      return null;
    }
    mergeCandidate(result.data);
    return result.data;
  };

  const handleAdvance = async (candidate, next) => {
    if (!api || !next?.action) return;
    setAdvancingId(candidate.id);
    logInfo(LOG_CONTEXT, 'advance candidate preparation', {
      requestId,
      candidateId: candidate.id,
      action: next.action,
      fromStatus: candidate.preparation_status,
    });
    const result = await api.advanceCandidatePreparation(candidate.id, next.action);
    setAdvancingId(null);
    if (!result.success) {
      logError(LOG_CONTEXT, 'advance candidate preparation failed', {
        candidateId: candidate.id,
        action: next.action,
        messages: result.messages,
      });
      showToast(
        result.messages?.[0] || 'Could not advance preparation',
        'error',
        'top-right',
        4000
      );
      return;
    }
    showToast(next.label, 'success', 'top-right', 2500);
    await applyPrepResponse(result.data);

    // Stamp thawed date when first advanced to thawing if still empty.
    if (next.action === 'mark_thawing' && !toDateInputValue(candidate.thawed_at)) {
      const list = Array.isArray(result.data?.candidates)
        ? result.data.candidates
        : null;
      const updated =
        list?.find(c => c.id === candidate.id) ||
        ({ ...candidate, preparation_status: 'thawing' });
      if (!toDateInputValue(updated.thawed_at)) {
        await patchMeta(updated, { thawed_at: todayDateInputValue() }, { silent: true });
      }
    }
  };

  /**
   * After Mark as shipped, fill shipped_at on accepted OCs that lack a sent date.
   */
  const onTransitionSuccess = async updated => {
    setRequest(updated);
    let list = Array.isArray(updated?.candidates) ? updated.candidates : null;
    if (!list) {
      const candRes = await api.getCandidates();
      if (candRes.success) list = candRes.data || [];
    }
    if (list) setCandidates(list);

    if (updated?.status === 'SHIPPED' && list?.length) {
      const today = todayDateInputValue();
      const needingSent = filterAcceptedCandidates(list).filter(
        c => !toDateInputValue(c.shipped_at)
      );
      for (const c of needingSent) {
        await patchMeta(c, { shipped_at: today }, { silent: true });
      }
      logInfo(LOG_CONTEXT, 'auto-filled shipped_at after mark_as_shipped', {
        requestId,
        count: needingSent.length,
      });
    }
  };

  /**
   * Lab confirms tech-approved OCs instead of the public portal.
   */
  const beforeVerifyForClient = async action => {
    if (action !== 'confirm_on_behalf_of_client') return true;
    const extraPayload = buildLabClientConfirmationPayload(candidates);
    const confirmableCount = extraPayload.candidate_decisions.length;
    if (!confirmableCount) {
      showToast(
        'No technically approved OCs to verify for the client',
        'warning',
        'top-right',
        4000
      );
      return false;
    }
    logInfo(LOG_CONTEXT, 'confirm_on_behalf_of_client', {
      requestId,
      action,
      confirmableCount,
    });
    return { extraPayload };
  };

  if (loading || !request || !api) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  const primary = getPrimaryTransitionForPage('preparation', request.status);
  const rowBusy = id => advancingId === id || savingId === id;
  const anyBusy = advancingId != null || savingId != null;

  const body = (
    <>
      <Button as={Link} to={basePath} variant="link" className="px-0 mb-2">
        ← Back to detail
      </Button>
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <h2 className="mb-0">Preparation — {request.request_number}</h2>
        <OcRequestStatusBadge status={request.status} />
      </div>
      <OcStageNav basePath={basePath} active="preparation" />

      <OcHandoffNotes actions={request.workflow_actions} />

      {awaitingClient ? (
        <>
          <Alert variant="warning" className="mb-3">
            Waiting on the client to confirm proposed OCs. You can verify on their
            behalf below — that skips the portal and marks technically approved OCs
            as accepted.
          </Alert>
          <div className="oc-glass-panel mb-4">
            <div className="oc-glass-body">
              <h3 className="h5 mb-3">OCs that will be accepted</h3>
              {!confirmable.length ? (
                <p className="text-muted mb-0">
                  No technically approved OCs. Complete technical review first.
                </p>
              ) : (
                <Table responsive hover size="sm" className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>OC</th>
                      <th>Technical decision</th>
                    </tr>
                  </thead>
                  <tbody>
                    {confirmable.map(c => (
                      <tr key={c.id}>
                        <td>{formatCandidateOcLabel(c)}</td>
                        <td>
                          {getOcTechDecisionLabel(c.technical_decision) || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </>
      ) : null}

      <div className="oc-glass-panel mb-4">
        <div className="oc-glass-body">
          <h3 className="h5 mb-3">Accepted OCs</h3>
          {!accepted.length ? (
            <p className="text-muted mb-0">
              No client-accepted candidates yet. Complete client confirmation first.
            </p>
          ) : (
            <Table responsive hover size="sm" className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>OC</th>
                  <th>In charge</th>
                  <th>Thawed</th>
                  <th>Sent</th>
                  <th>Preparation</th>
                  <th className="text-end">Next step</th>
                </tr>
              </thead>
              <tbody>
                {accepted.map(c => {
                  const next = getNextPreparationAction(c.preparation_status || 'pending');
                  const busy = rowBusy(c.id);
                  return (
                    <tr key={c.id}>
                      <td>{formatCandidateOcLabel(c)}</td>
                      <td style={{ minWidth: 180 }}>
                        <AssigneeUserSelect
                          controlId={`prep-owner-${c.id}`}
                          value={getPreparationOwnerId(c)}
                          options={assigneeOptions}
                          emptyLabel="Unassigned"
                          disabled={readOnly || anyBusy}
                          onChange={userId =>
                            patchMeta(c, { preparation_owner: userId })
                          }
                        />
                      </td>
                      <td style={{ minWidth: 140 }}>
                        <Form.Control
                          type="date"
                          size="sm"
                          value={toDateInputValue(c.thawed_at)}
                          disabled={readOnly || anyBusy}
                          onChange={e =>
                            patchMeta(c, {
                              thawed_at: e.target.value || null,
                            })
                          }
                          aria-label={`Thawed date for ${formatCandidateOcLabel(c)}`}
                        />
                      </td>
                      <td style={{ minWidth: 140 }}>
                        <Form.Control
                          type="date"
                          size="sm"
                          value={toDateInputValue(c.shipped_at)}
                          disabled={readOnly || anyBusy}
                          onChange={e =>
                            patchMeta(c, {
                              shipped_at: e.target.value || null,
                            })
                          }
                          aria-label={`Sent date for ${formatCandidateOcLabel(c)}`}
                        />
                      </td>
                      <td>
                        {getPreparationStatusLabel(c.preparation_status || 'pending')}
                      </td>
                      <td className="text-end">
                        {next && !readOnly ? (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            disabled={busy || anyBusy}
                            onClick={() => handleAdvance(c, next)}
                          >
                            {busy && advancingId === c.id ? (
                              <>
                                <Spinner
                                  animation="border"
                                  size="sm"
                                  className="me-1"
                                />
                                Updating…
                              </>
                            ) : (
                              next.label
                            )}
                          </Button>
                        ) : (
                          <span className="text-muted small">Ready</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>
      </div>

      {!readOnly && primary ? (
        <OcTransitionBar
          api={api}
          requestId={requestId}
          request={request}
          action={primary.action}
          label={primary.label || 'Complete'}
          secondaryActions={[]}
          disabled={
            awaitingClient && confirmable.length === 0
          }
          beforeTransition={beforeVerifyForClient}
          onSuccess={onTransitionSuccess}
        />
      ) : null}
    </>
  );

  if (mode === 'labPublic') return body;
  return <OcWorkspaceShell wide theme="yellow">{body}</OcWorkspaceShell>;
};

export default OcRequestPreparationPage;
